/* Market pairs are explicit: these are separate inventory-bearing products.
 * Shared SmartPak Max intentionally has no replacement. */
window.MoreeMarketCart = (() => {
  const canadaPairs = {
    44527573205034: 44246506995754,
    44527573172266: 44246507094058,
    44527573106730: 44246603989034,
  };
  const markets = {
    CA: {pairs: canadaPairs, currency: 'CAD', en: 'Canada', fr: 'le Canada', currencyFr: 'canadiens'},
    US: {pairs: Object.fromEntries(Object.entries(canadaPairs).map(([us, ca]) => [ca, Number(us)])), currency: 'USD', en: 'the US', fr: 'les États-Unis', currencyFr: 'américains'},
  };
  const journalKey = 'moree-market-cart-v2';
  const legacyJournalKey = 'moree-canada-cart-v1';
  const noticeKey = 'moree-market-cart-complete';
  const root = () => window.Shopify?.routes?.root || '/';
  const country = () => document.querySelector('input[name="country_code"]')?.defaultValue;
  const french = () => document.documentElement.lang.startsWith('fr');
  const copy = (en, fr) => french() ? fr : en;
  const signature = item => JSON.stringify([Number(item.variant_id || item.id),
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
  const sourceLines = (cart, destination = country()) => cart.items.filter(item => markets[destination]?.pairs[item.variant_id]);
  const readJournal = () => JSON.parse(sessionStorage.getItem(journalKey) || sessionStorage.getItem(legacyJournalKey) || 'null');
  const clearJournal = () => { sessionStorage.removeItem(journalKey); sessionStorage.removeItem(legacyJournalKey); };
  const findSource = (cart, source) => cart.items.find(item => item.key === source.key) ||
    cart.items.find(item => signature(item) === signature(source));
  const count = (cart, group) => cart.items.filter(item => signature(item) === group.signature)
    .reduce((sum, item) => sum + item.quantity, 0);
  let active;
  let switching = false;
  let dialog;
  let restoreFocus;
  let retryAction;
  let navigating = false;
  const approvedSubmits = new WeakSet();

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
    const response = await fetch(`${root()}${path}`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error('Cart request failed');
    return data;
    } finally { clearTimeout(timeout); }
  }

  function showStatus(failed = false, retry = run, destination = country()) {
    const market = markets[destination] || markets.CA;
    retryAction = retry;
    if (!dialog) {
      const style = document.createElement('style');
      style.textContent = `.moree-market-dialog{box-sizing:border-box;width:calc(100% - 32px);max-width:32rem;padding:var(--space-24,24px);border:1px solid rgb(var(--color-foreground));border-radius:var(--radius-md,8px);background:rgb(var(--color-background));color:rgb(var(--color-foreground));font:inherit;overflow-wrap:anywhere}.moree-market-dialog::backdrop{background:rgba(0,0,0,.45)}.moree-market-dialog-actions[hidden]{display:none}.moree-market-dialog p{margin:0}.moree-market-dialog-actions{display:flex;flex-wrap:wrap;gap:var(--space-16,16px);margin-top:var(--space-16,16px)}.moree-market-dialog button{min-height:44px;min-width:44px;white-space:normal}.moree-market-notice{margin:0 0 var(--space-16,16px);overflow-wrap:anywhere}`;
      document.head.append(style);
      dialog = document.createElement('dialog');
      dialog.className = 'moree-market-dialog';
      dialog.setAttribute('aria-labelledby', 'moree-market-message');
      dialog.innerHTML = '<p id="moree-market-message" role="status" aria-live="polite" tabindex="-1"></p><div class="moree-market-dialog-actions" hidden><button type="button" class="button" data-market-retry></button><button type="button" class="button button--secondary" data-market-close></button></div>';
      dialog.querySelector('[data-market-retry]').textContent = copy('Try again', 'Réessayer');
      dialog.querySelector('[data-market-close]').textContent = copy('Close', 'Fermer');
      dialog.querySelector('[data-market-retry]').addEventListener('click', () => {
        retryAction();
      });
      dialog.querySelector('[data-market-close]').addEventListener('click', () => {
        dialog.close();
        restoreFocus?.focus();
      });
      dialog.addEventListener('cancel', event => {
        if (active || switching) event.preventDefault();
      });
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const buttons = [...dialog.querySelectorAll('button')].filter(button => !button.parentElement.hidden);
        const first = buttons[0] || dialog.querySelector('p');
        const last = buttons.at(-1) || first;
        if (!buttons.length || (event.shiftKey && document.activeElement === first) ||
            (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      });
      dialog.addEventListener('close', () => restoreFocus?.focus());
      document.body.append(dialog);
    }
    dialog.querySelector('p').textContent = failed
      ? copy(`We couldn’t finish updating your cart for ${market.en}. Please retry before checking out. Your items have not been discarded.`, `La mise à jour de votre panier pour ${market.fr} n’a pas pu être terminée. Veuillez réessayer avant de passer à la caisse. Vos articles n’ont pas été supprimés.`)
      : copy(`Updating your cart for ${market.en}… Quantities will stay the same. Prices will be shown in ${market.currency}.`, `Mise à jour de votre panier pour ${market.fr}… Les quantités restent les mêmes. Les prix seront affichés en dollars ${market.currencyFr}.`);
    dialog.querySelector('.moree-market-dialog-actions').hidden = !failed;
    if (!dialog.open) { restoreFocus ||= document.activeElement; dialog.showModal(); }
    (failed ? dialog.querySelector('[data-market-retry]') : dialog.querySelector('p')).focus();
  }

  function makeJournal(cart) {
    const sources = sourceLines(cart).map(item => {
      if (item.selling_plan_allocation || item.parent_relationship) throw new Error('Unsupported line relationship');
      return { key: item.key, variant_id: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
    });
    const groups = [];
    for (const item of sources) {
      const target = { id: markets[country()].pairs[item.variant_id], properties: item.properties };
      const key = signature(target);
      let group = groups.find(entry => entry.signature === key);
      if (!group) {
        group = { ...target, signature: key, quantity: 0 };
        group.quantity = count(cart, group);
        groups.push(group);
      }
      group.quantity += item.quantity;
    }
    return { token: cart.token, market: country(), sources, groups };
  }

  async function undoPendingCopies(cart, journal) {
    const previous = markets[journal.market || 'CA'];
    if (!previous) throw new Error('Unknown previous market');
    const remaining = journal.sources.filter(source => {
      const current = findSource(cart, source);
      if (current && current.quantity !== source.quantity) throw new Error('Source quantity changed');
      return current;
    });
    const updates = {};
    const expected = [];
    for (const group of journal.groups) {
      const unremovedQuantity = remaining.filter(source => signature({
        id: previous.pairs[source.variant_id], properties: source.properties,
      }) === group.signature).reduce((sum, source) => sum + source.quantity, 0);
      const retained = Math.min(count(cart, group), group.quantity - unremovedQuantity);
      let excess = count(cart, group) - retained;
      expected.push({ group, retained });
      // Remove only copies whose originals still exist; retain pre-existing destination units.
      for (const item of cart.items.filter(item => signature(item) === group.signature)) {
        if (!excess) break;
        const remove = Math.min(item.quantity, excess);
        updates[item.key] = item.quantity - remove;
        excess -= remove;
      }
    }
    if (Object.keys(updates).length) {
      await request('cart/update.js', { updates });
      cart = await request('cart.js');
      if (expected.some(({group, retained}) => count(cart, group) !== retained)) throw new Error('Unverified recovery');
    }
    return cart;
  }

  async function replace() {
    let cart = await request('cart.js');
    let journal = readJournal();
    let recovered = false;
    if (journal?.token !== cart.token) { journal = null; clearJournal(); }
    if (journal && (journal.market || 'CA') !== country()) {
      showStatus();
      cart = await undoPendingCopies(cart, journal);
      clearJournal();
      journal = null;
      recovered = true;
    }
    if (!sourceLines(cart).length) {
      clearJournal();
      if (recovered || (journal && journal.groups.every(group => count(cart, group) >= group.quantity))) {
        sessionStorage.setItem(noticeKey, country());
        return true;
      }
      return false;
    }
    showStatus();
    if (!journal) {
      journal = makeJournal(cart);
      sessionStorage.setItem(journalKey, JSON.stringify(journal));
      sessionStorage.removeItem(legacyJournalKey);
    }
    // A changed source is a new shopper decision, not permission to discard it.
    for (const source of journal.sources) {
      const current = findSource(cart, source);
      if (current && current.quantity !== source.quantity) throw new Error('Source quantity changed');
    }
    const additions = journal.groups.map(group => ({
      id: group.id, properties: group.properties, quantity: group.quantity - count(cart, group),
    })).filter(item => item.quantity > 0);
    if (additions.length) {
      // If an add succeeds but its response is lost, the saved target totals make retry idempotent.
      await request('cart/add.js', { items: additions });
      cart = await request('cart.js');
    }
    if (journal.groups.some(group => count(cart, group) < group.quantity)) throw new Error('Incomplete replacement');
    const updates = {};
    for (const source of journal.sources) {
      const current = findSource(cart, source);
      if (current) {
        if (current.quantity !== source.quantity) throw new Error('Source changed');
        updates[current.key] = 0;
      }
    }
    if (Object.keys(updates).length) await request('cart/update.js', { updates });
    cart = await request('cart.js');
    if (journal.sources.some(source => findSource(cart, source)) ||
        journal.groups.some(group => count(cart, group) < group.quantity)) throw new Error('Unverified replacement');
    clearJournal();
    sessionStorage.setItem(noticeKey, country());
    // Another source line added during the transaction is handled before checkout.
    if (sourceLines(cart).length) return replace();
    return true;
  }

  function run() {
    if (!markets[country()] || navigating) return Promise.resolve(!navigating);
    if (active) return active;
    if (!dialog?.open) restoreFocus = document.activeElement;
    const main = document.querySelector('main');
    const wasInert = main?.inert;
    if (main) main.inert = true;
    const work = async () => {
      try {
        const changed = await replace();
        if (changed) {
          navigating = true;
          window.location.reload();
          return false;
        }
        dialog?.close();
        return true;
      } catch {
        showStatus(true);
        return false;
      }
    };
    active = (navigator.locks ? navigator.locks.request('moree-market-cart', work) : work())
      .finally(() => { active = null; if (main) main.inert = wasInert; });
    return active;
  }

  function productDestination(next) {
    const pairs = [
      ['pilot-starter-kit-best-seller', 'pilot-starter-pak-us'],
      ['moree-pakbak', 'pakbak-us'],
      ['smartpak-mini-2pak-2-500g-reusable-food-coffee-pouches', 'smartpak-mini-2pak-2-500g-reusable-food-coffee-pouches-us'],
      ['smartpak-max-5kg-50-pak', 'smartpak-max-5kg-50-pak-us'],
    ];
    const url = new URL(window.location.href);
    const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
    const pair = match && pairs.find(handles => handles.includes(match[1]));
    if (!pair) return null;
    const handle = pair[next === 'US' ? 1 : 0];
    if (handle === match[1]) return null;
    url.pathname = `${root().replace(/\/?$/, '/')}products/${handle}`;
    url.searchParams.delete('variant');
    url.searchParams.delete('country');
    return url.pathname + url.search + url.hash;
  }

  async function select(form, input, next) {
    if (switching || active || navigating) return;
    switching = true;
    if (!dialog?.open) restoreFocus = document.activeElement;
    try {
      if (input.name === 'country_code' && markets[next]) {
        const productUrl = productDestination(next);
        if (productUrl) {
          let destination = form.querySelector('[name="return_to"]');
          if (!destination) {
            destination = document.createElement('input');
            destination.type = 'hidden'; destination.name = 'return_to'; form.append(destination);
          }
          destination.value = productUrl;
        }
        const cart = await request('cart.js');
        if (sourceLines(cart, next).length) {
          let destination = form.querySelector('[name="return_to"]');
          if (!destination) {
            destination = document.createElement('input');
            destination.type = 'hidden'; destination.name = 'return_to'; form.append(destination);
          }
          const url = new URL(`${root()}cart`, window.location.origin);
          const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          destination.value = url.pathname + url.search;
        }
      }
      input.value = next;
      form.submit();
    } catch { showStatus(true, () => select(form, input, next), next); }
    finally { switching = false; }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!markets[country()] || approvedSubmits.has(form)) return;
    if (event.submitter?.name !== 'checkout' && !['cart', 'CartDrawer-Form'].includes(form.id)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const submitter = event.submitter;
    run().then(ready => {
      if (!ready) return;
      approvedSubmits.add(form);
      try {
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } finally { approvedSubmits.delete(form); }
    });
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || !markets[country()] || !/^\/checkout(?:[/?]|$)/.test(new URL(link.href).pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    run().then(ready => { if (ready) window.location.assign(link.href); });
  }, true);

  queueMicrotask(() => {
    if (!markets[country()]) return;
    const cartPage = /\/cart\/?$/.test(window.location.pathname);
    let completed = false;
    try { completed = sessionStorage.getItem(noticeKey); } catch { /* Repair fails safely if storage is unavailable. */ }
    if (cartPage && completed === country()) {
      sessionStorage.removeItem(noticeKey);
      const heading = document.querySelector('cart-items .title-wrapper-with-link');
      if (heading) {
        const notice = document.createElement('p');
        notice.className = 'moree-market-notice'; notice.setAttribute('role', 'status');
        const market = markets[country()];
        notice.textContent = copy(`Your Moree items have been updated for ${market.en}. Quantities are unchanged; prices are in ${market.currency}.`, `Vos articles Moree ont été mis à jour pour ${market.fr}. Les quantités sont inchangées; les prix sont en dollars ${market.currencyFr}.`);
        notice.style.margin = '0 0 var(--space-16, 16px)';
        heading.before(notice);
      }
    }
    if (cartPage || [...document.querySelectorAll('cart-drawer a[href]')].some(link => markets[country()].pairs[new URL(link.href).searchParams.get('variant')])) run();
  });
  return { select, run };
})();

if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer').classList.remove('country-selector-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        if (form) window.MoreeMarketCart.select(form, this.elements.input, event.currentTarget.dataset.value);
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}

